/**
 * ExternalRelease list component with table view and actions.
 */

import { Table, Button, Space, Tag, Tooltip, Modal, Badge, Popover, Progress } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { ExternalRelease } from '@/types';
import { formatDate, formatFileSize } from '@/utils';
import { formatVehicleList } from '@/utils/vehicle';
import { resolveUploadPercent } from '@/utils/uploadProgress';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { CopyExternalLinkButton } from '@/components/packages/CopyExternalLinkButton';
import { versionTypeLabel } from '@/constants/externalRelease';

const formatSpeed = (bps: number) =>
  bps > 1024 * 1024
    ? `${(bps / 1024 / 1024).toFixed(1)} MB/s`
    : `${(bps / 1024).toFixed(0)} KB/s`;

interface ExternalReleaseListProps {
  releases: ExternalRelease[];
  loading: boolean;
  pagination: TablePaginationConfig;
  projectId?: number;
  onEdit: (release: ExternalRelease) => void;
  onDelete?: (release: ExternalRelease) => void;
  onViewLogs?: (release: ExternalRelease) => void;
  onPageChange: (page: number, pageSize: number) => void;
}

export const ExternalReleaseList: React.FC<ExternalReleaseListProps> = ({
  releases,
  loading,
  pagination,
  projectId,
  onEdit,
  onDelete,
  onViewLogs,
  onPageChange,
}) => {
  const { isAdmin, isExternal } = useAuth();
  const navigate = useNavigate();
  const { progress } = useUploadProgressStore();

  const confirmDelete = (release: ExternalRelease) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除 "${release.name}" 吗？此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => onDelete?.(release),
    });
  };

  const columns: ColumnsType<ExternalRelease> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name: string, record) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span className="font-medium">{name}</span>
          {record.source === 'rpa' && <Tag color="blue" className="ml-1">RPA</Tag>}
          <CopyExternalLinkButton
            id={record.id}
            kind="release"
            projectId={projectId ?? record.project?.id}
          />
        </div>
      ),
    },
    {
      title: '版本类型',
      dataIndex: 'version_type',
      key: 'version_type',
      width: 110,
      // RPA 记录显示 RPA 的版本类型原文；BSP 记录显示所选类型，未选择显示「-」
      render: (versionType: ExternalRelease['version_type'], record: ExternalRelease) => {
        if (record.source === 'rpa') {
          return <Tag>{record.rpa_version_type || '-'}</Tag>;
        }
        if (!versionType) return <span className="text-gray-600">-</span>;
        return (
          <Tag color={versionType === 'official' ? 'green' : undefined}>
            {versionTypeLabel(versionType)}
          </Tag>
        );
      },
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      // 实际宽度上限由 .bsp-clamp-3 的 max-width 控制（auto 布局下 width 仅为建议值）。
      width: 260,
      ellipsis: false,
      render: (description: string) => {
        if (!description) return <span className="text-gray-600">-</span>;
        // 超长说明会把整行撑高，限制最多 3 行，完整内容通过悬浮提示查看。
        return (
          <Tooltip title={description} placement="topLeft">
            <div className="bsp-clamp-3 text-gray-600">{description}</div>
          </Tooltip>
        );
      },
    },
    {
      title: '车型',
      dataIndex: 'vehicle_model',
      key: 'vehicle_model',
      width: 120,
      render: (model: string[]) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span>{formatVehicleList(model)}</span>
        </div>
      ),
    },
    {
      title: '子车型',
      dataIndex: 'vehicle_subtype',
      key: 'vehicle_subtype',
      width: 120,
      render: (subtype: string[]) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span>{formatVehicleList(subtype)}</span>
        </div>
      ),
    },
    {
      title: '硬件平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (platform: string) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span>{platform || '-'}</span>
        </div>
      ),
    },
    {
      title: '上传状态',
      dataIndex: 'upload_status',
      key: 'upload_status',
      width: 90,
      render: (status: string, record: ExternalRelease) => {
        const map: Record<string, { status: 'success' | 'processing' | 'error' | 'default'; text: string }> = {
          done: { status: 'success', text: '完成' },
          uploading: { status: 'processing', text: '上传中' },
          failed: { status: 'error', text: '失败' },
          pending: { status: 'default', text: '等待' },
        };
        const cfg = map[status] ?? { status: 'default', text: status || '完成' };

        // 这一列描述「这个产物包还在动吗」，而不只是 OBS 那一段。第三方上传
        // （产线包打包→立昇，可能跑十几分钟）期间 OBS 早已 done，若只看
        // upload_status 会显示「完成」，用户以为没事了。此时覆盖成「上传中」。
        if (status !== 'uploading' && record.third_party_status === 'uploading') {
          return (
            <Tooltip title="正在上传第三方平台">
              <Badge status="processing" text="上传中" />
            </Tooltip>
          );
        }

        const badge = <Badge status={cfg.status} text={cfg.text} />;

        if (status !== 'uploading') return badge;

        const files = record.files ?? [];
        const docEntries: { key: string; label: string; stuckName?: string }[] = [
          { key: `externalrelease-${record.id}-doc-release_note`, label: 'Momenta Release Note',
            stuckName: !record.momenta_release_note_url && record.momenta_release_note_name ? record.momenta_release_note_name : undefined },
          { key: `externalrelease-${record.id}-doc-test_report`, label: 'Momenta 测试报告',
            stuckName: !record.momenta_test_report_url && record.momenta_test_report_name ? record.momenta_test_report_name : undefined },
        ];
        const activeDocEntries = docEntries.filter(({ key, stuckName }) =>
          (progress[key] && progress[key].status !== 'done') || !!stuckName
        );
        if (!files.length && !activeDocEntries.length) return badge;

        const content = (
          <div style={{ minWidth: 280, maxWidth: 360 }}>
            {files.map((file) => {
              const key = `externalrelease-${record.id}-file-${file.id}`;
              const entry = progress[key];
              const pct = resolveUploadPercent(file, entry);
              const fileStatus = entry?.status ?? file.upload_status ?? 'uploading';
              const progressStatus =
                fileStatus === 'failed' ? 'exception' :
                fileStatus === 'done'   ? 'success'   : 'active';
              return (
                <div key={file.id} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#666' }}>{file.file_type?.name}</span>
                    <span style={{ color: '#999' }}>{formatFileSize(file.file_size)}</span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 2, wordBreak: 'break-all' }}>{file.file_name}</div>
                  <Progress percent={pct} size="small" status={progressStatus} />
                </div>
              );
            })}
            {activeDocEntries.map(({ key, label, stuckName }) => {
              const entry = progress[key];
              const isStuck = !entry && !!stuckName;
              const displayName = isStuck ? stuckName : entry?.rawFile?.name;
              const progressStatus = (isStuck || entry?.status === 'failed') ? 'exception' : 'active';
              const pct = isStuck ? 0 : (entry?.percent ?? 0);
              return (
                <div key={key} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{label}</div>
                  {displayName && (
                    <div style={{ fontSize: 12, marginBottom: 2, wordBreak: 'break-all', color: '#999' }}>{displayName}</div>
                  )}
                  <Progress percent={pct} size="small" status={progressStatus} />
                  {!isStuck && entry?.currentSpeed !== undefined && entry.currentSpeed > 0 && (
                    <span style={{ fontSize: 11, color: '#999' }}>{formatSpeed(entry.currentSpeed)}</span>
                  )}
                </div>
              );
            })}
          </div>
        );

        return (
          <Popover content={content} title="文件上传进度" trigger="hover" placement="left">
            {badge}
          </Popover>
        );
      },
    },
    {
      title: '关联文件',
      key: 'packages',
      render: (_: unknown, record: ExternalRelease) => {
        const pkgs = record.packages;
        if (!pkgs?.length) return <span className="text-gray-400">-</span>;
        return (
          <Space size={[4, 4]} wrap>
            {pkgs.map((p) => (
              <Tooltip key={p.id} title={p.name}>
                <Tag color="blue" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 0 }}>{p.name}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: '创建人',
      key: 'created_by',
      width: 90,
      render: (_, record) =>
        record.created_by?.display_name || record.created_by?.username || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date: string) => formatDate(date),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/external-release/${record.id}${projectId ? `?projectId=${projectId}` : ''}`)}
            />
          </Tooltip>

          {/* 第三方上传进行中禁止编辑（FR-021）。失败是终态，不锁定（FR-022）。
              third_party_editable 为空是老数据/未启用插件，默认可编辑。外部用户只读。 */}
          <Tooltip
            title={
              isExternal
                ? '外部用户无编辑权限'
                : record.third_party_editable === false
                  ? '正在上传第三方平台，完成后才能编辑'
                  : '编辑'
            }
          >
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              disabled={isExternal || record.third_party_editable === false}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          {onViewLogs && (
            <Tooltip title="操作日志">
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => onViewLogs(record)}
              />
            </Tooltip>
          )}

          {isAdmin && onDelete && (
            <Tooltip title="删除">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => confirmDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table<ExternalRelease>
      columns={columns}
      dataSource={releases}
      loading={loading}
      rowKey="id"
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        onChange: onPageChange,
      }}
      scroll={{}}
      size="middle"
    />
  );
};

export default ExternalReleaseList;
